import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@/lib/utils"

const TooltipProvider = TooltipPrimitive.Provider

const Tooltip = TooltipPrimitive.Root

const TooltipTrigger = TooltipPrimitive.Trigger

/**
 * The tooltip surface, shared with the chart tooltips that cannot use radix.
 * Keep in sync with the MuiTooltip overrides in the nui theme.
 */
export const tooltipSurface =
  "rounded-tile border border-edge bg-popover/95 px-2.5 py-1.5 font-rad text-caption tracking-[.06em] text-fg scrim shadow-pop"

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  //portaled so it is not clipped by scroll containers like the playerlist
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        tooltipSurface,
"z-50 overflow-hidden animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

/**
 * Drop-in replacement for a native `title` attribute, so every hover hint gets
 * the panel surface instead of the browser's own box.
 * Renders the child untouched when there is nothing to show.
 */
type HoverTipProps = {
  label?: React.ReactNode;
  side?: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>["side"];
  className?: string;
  children: React.ReactNode;
};
function HoverTip({ label, side, className, children }: HoverTipProps) {
  if (label === undefined || label === null || label === "") return <>{children}</>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side} className={className}>{label}</TooltipContent>
    </Tooltip>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, HoverTip }
